import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { BRAND, EVENT_TYPE_OPTIONS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  contact: "",
  event_date: "",
  event_type: "",
  guest_count: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!form.contact.trim() || form.contact.trim().length < 5)
      e.contact = "Please share a phone or email.";
    if (!form.event_type.trim()) e.event_type = "Please pick an event type.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — your inquiry has reached us. We'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit just now. Please WhatsApp or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const buildWhatsappLink = () => {
    const lines = [
      "Namaste, I'd like to enquire about Sri Bhavani Kalyana Mandira.",
      form.name && `Name: ${form.name}`,
      form.contact && `Contact: ${form.contact}`,
      form.event_type && `Event: ${form.event_type}`,
      form.event_date && `Date: ${form.event_date}`,
      form.guest_count && `Guests: ${form.guest_count}`,
      form.message && `Notes: ${form.message}`,
    ].filter(Boolean);
    return `https://wa.me/${BRAND.whatsappRaw}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const buildMailto = () => {
    const subject = encodeURIComponent(
      `Venue Enquiry${form.event_type ? " — " + form.event_type : ""}`
    );
    const body = encodeURIComponent(
      [
        `Name: ${form.name}`,
        `Contact: ${form.contact}`,
        form.event_date && `Event Date: ${form.event_date}`,
        form.event_type && `Event Type: ${form.event_type}`,
        form.guest_count && `Estimated Guests: ${form.guest_count}`,
        "",
        form.message,
      ]
        .filter(Boolean)
        .join("\n")
    );
    return `mailto:${BRAND.email}?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-accent/40"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="eyebrow" data-testid="contact-overline">Contact</p>
          <h2
            className="mt-4 font-headings text-3xl md:text-5xl text-primary leading-tight"
            data-testid="contact-heading"
          >
            Plan your celebration with us.
          </h2>
          <p className="mt-5 font-body text-base md:text-lg text-foreground/75">
            Get in touch to check available dates, request a custom quote, or schedule a walkthrough
            of the venue.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-background p-8 md:p-10 border border-border"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Full Name"
                id="cf-name"
                required
                value={form.name}
                onChange={onChange("name")}
                error={errors.name}
                testid="contact-input-name"
                placeholder="Your full name"
              />
              <Field
                label="Phone or Email"
                id="cf-contact"
                required
                value={form.contact}
                onChange={onChange("contact")}
                error={errors.contact}
                testid="contact-input-contact"
                placeholder="+91 9 / you@email.com"
              />
              <Field
                label="Proposed Event Date"
                id="cf-date"
                type="date"
                value={form.event_date}
                onChange={onChange("event_date")}
                testid="contact-input-date"
              />
              <SelectField
                label="Type of Event"
                id="cf-type"
                required
                value={form.event_type}
                onChange={onChange("event_type")}
                error={errors.event_type}
                options={EVENT_TYPE_OPTIONS}
                testid="contact-input-event-type"
              />
              <Field
                label="Estimated Guest Count"
                id="cf-guests"
                value={form.guest_count}
                onChange={onChange("guest_count")}
                testid="contact-input-guests"
                placeholder="e.g. 250"
              />
              <Field
                label="Phone (optional alt.)"
                id="cf-phone-alt"
                value=""
                onChange={() => {}}
                disabled
                testid="contact-input-spacer"
                placeholder="—"
                visuallyOptional
                hidden
              />
            </div>

            <div className="mt-6">
              <label
                htmlFor="cf-message"
                className="block font-ui text-[11px] uppercase tracking-[0.22em] text-foreground/70 mb-2"
              >
                Your Message
              </label>
              <textarea
                id="cf-message"
                rows={4}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Tell us about your celebration, traditions, or any specific requirements…"
                className="w-full bg-transparent border border-border rounded-sm px-4 py-3 font-body text-base focus:outline-none focus:border-primary transition-colors resize-none"
                data-testid="contact-input-message"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-sm bg-primary text-primary-foreground font-ui text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors disabled:opacity-60"
                data-testid="contact-form-submit"
              >
                {submitting ? "Sending…" : "Send Inquiry"}
              </button>
              <a
                href={buildWhatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm border border-primary text-primary font-ui text-[12px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid="contact-form-whatsapp"
              >
                <MessageCircle size={16} /> Send via WhatsApp
              </a>
              <a
                href={buildMailto()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm border border-border text-foreground font-ui text-[12px] uppercase tracking-[0.22em] hover:border-foreground transition-colors"
                data-testid="contact-form-email"
              >
                <Mail size={16} /> Email Us
              </a>
            </div>
          </form>

          {/* Details + map */}
          <aside className="lg:col-span-5 flex flex-col gap-8" data-testid="contact-details">
            <div className="bg-background border border-border p-8 md:p-10">
              <h3 className="font-headings text-2xl text-primary">Visit Us</h3>
              <div className="mt-6 space-y-5 font-body text-foreground/85">
                <DetailRow icon={MapPin} testid="contact-address">
                  <a
                    href={BRAND.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                  >
                    {BRAND.address}
                  </a>
                </DetailRow>
                <DetailRow icon={Phone} testid="contact-phone">
                  <a href={`tel:${BRAND.phoneRaw}`} className="link-underline">
                    {BRAND.phone}
                  </a>
                </DetailRow>
                <DetailRow icon={MessageCircle} testid="contact-whatsapp">
                  <a
                    href={`https://wa.me/${BRAND.whatsappRaw}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                  >
                    {BRAND.whatsapp} (WhatsApp)
                  </a>
                </DetailRow>
                <DetailRow icon={Mail} testid="contact-email">
                  <a href={`mailto:${BRAND.email}`} className="link-underline">
                    {BRAND.email}
                  </a>
                </DetailRow>
                <DetailRow icon={Clock} testid="contact-hours">
                  {BRAND.hours}
                </DetailRow>
              </div>
              <a
                href={BRAND.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.22em] text-secondary hover:text-primary transition-colors"
                data-testid="contact-directions-link"
              >
                Get Directions <ExternalLink size={14} />
              </a>
            </div>

            <div
              className="aspect-[4/3] w-full overflow-hidden border border-border bg-muted"
              data-testid="contact-map"
            >
              <iframe
                title="Sri Bhavani Kalyana Mandira location"
                src={BRAND.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  id,
  type = "text",
  value,
  onChange,
  required,
  error,
  placeholder,
  testid,
  disabled,
  hidden,
}) {
  if (hidden) return null;
  return (
    <div className={disabled ? "opacity-0 pointer-events-none hidden md:block" : ""}>
      <label
        htmlFor={id}
        className="block font-ui text-[11px] uppercase tracking-[0.22em] text-foreground/70 mb-2"
      >
        {label} {required && <span className="text-primary">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`w-full bg-transparent border ${
          error ? "border-primary" : "border-border"
        } rounded-sm px-4 py-3 font-body text-base focus:outline-none focus:border-primary transition-colors`}
        data-testid={testid}
      />
      {error && (
        <p
          className="mt-1.5 font-ui text-xs text-primary"
          data-testid={`${testid}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, id, value, onChange, options, required, error, testid }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-ui text-[11px] uppercase tracking-[0.22em] text-foreground/70 mb-2"
      >
        {label} {required && <span className="text-primary">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        required={required}
        className={`w-full bg-transparent border ${
          error ? "border-primary" : "border-border"
        } rounded-sm px-4 py-3 font-body text-base focus:outline-none focus:border-primary transition-colors`}
        data-testid={testid}
      >
        <option value="">Select an event type…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {error && (
        <p
          className="mt-1.5 font-ui text-xs text-primary"
          data-testid={`${testid}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, children, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <Icon size={18} strokeWidth={1.5} className="mt-1 text-secondary flex-shrink-0" />
      <p className="font-body text-base leading-relaxed">{children}</p>
    </div>
  );
}
